import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';

export type UiAvatarUser = {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url?: string | null;
  avatar_large_url?: string | null;
  is_online?: boolean;
};

let uniqueId = 0;

@Component({
  selector: 'bl-avatar',
  imports: [CommonModule, IonicModule],
  templateUrl: './ui-avatar.component.html',
  styleUrls: ['./ui-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAvatarComponent implements OnInit {
  @Input() user!: UiAvatarUser;
  /**
   * In pixels.
   */
  @Input() size = 64;
  /**
   * Allows clicks on avatar to navigate to user profile.
   */
  @Input() clickable = true;
  /**
   * Show online indicator in bottom right corner.
   */
  @Input() showOnlineIndicator = true;

  initials = '';

  router = inject(Router);
  cdref = inject(ChangeDetectorRef);

  uniqueId = uniqueId++;

  ngOnInit() {
    const tuple = [this.user.first_name ?? '', this.user.last_name ?? ''];
    this.initials = tuple.map(name => name[0]?.toUpperCase()).join('');
  }

  // Backend exposes two image variants: avatar_url is the 150x150 "medium"
  // and avatar_large_url is the 500x500 "large". Width descriptors let the
  // browser pick the right one based on the rendered size and device DPR
  // (so a 100x100 avatar on a 3x screen requests 300px and picks `large`).
  get avatarSrcset(): string | null {
    if (!this.user.avatar_url) return null;
    if (!this.user.avatar_large_url) return null;
    return `${this.user.avatar_url} 150w, ${this.user.avatar_large_url} 500w`;
  }

  get fullName(): string | null {
    const name = `${this.user.first_name ?? ''} ${
      this.user.last_name ?? ''
    }`.trim();
    return name || null;
  }

  navigateToUserProfile() {
    if (this.clickable) {
      this.router.navigateByUrl(`/profile/${this.user.id}`);
    }
  }
}
